"use client";

import { useMemo, useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import styles from "../works.module.scss";

type Work = {
  id: string;
  title: string;
  body?: string;
  publishedAt: string;
  // microCMSの「コンテンツ参照 - 実績一覧のカテゴリ」を想定
  // 参照先のテキストフィールドIDが `category` のため、その形も許容
  category?: unknown;
  categories?: unknown;
  tag?: { id: string; name: string } | { id: string; name: string }[];
  thumbnail?: { url: string };
  categoryKey: string;
};

type WorksGridProps = {
  works: Work[];
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function pickString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function pickIdString(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  return "";
}

function getWorksCategoryName(value: unknown): string {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (Array.isArray(value)) {
    const first = value[0] as unknown;
    return first ? getWorksCategoryName(first) : "";
  }
  if (!isRecord(value)) return "";

  // 参照先が「name」ではなく「title」等のケースも吸収
  const directCategoryText = pickString(value.category);
  const nestedCategory = isRecord(value.category) ? value.category : undefined;

  return (
    directCategoryText ||
    pickString(value.name) ||
    pickString(value.title) ||
    pickString(nestedCategory?.name) ||
    pickString(nestedCategory?.title) ||
    pickString(value.label) ||
    pickString(value.value) ||
    ""
  );
}

function getWorksCategoryLabel(work: unknown): string {
  if (!isRecord(work)) return "";
  return (
    getWorksCategoryName(work.category) ||
    getWorksCategoryName(work.categories) ||
    ""
  );
}

function getWorksCategoryFilterKey(work: unknown): string {
  if (!isRecord(work)) return "";
  const c = work.category ?? work.categories;
  if (!c) return "";
  if (typeof c === "string") return c;

  if (Array.isArray(c)) {
    const first = c[0] as unknown;
    if (!first) return "";
    if (typeof first === "string") return first;
    if (isRecord(first)) return pickIdString(first.id);
    return "";
  }

  if (isRecord(c)) return pickIdString(c.id);
  return "";
}

export default function WorksGrid({ works }: WorksGridProps) {
  const [activeFilter, setActiveFilter] = useState("all");
  const [filteredWorks, setFilteredWorks] = useState(works);

  const filters = useMemo(() => {
    const map = new Map<string, string>();
    works.forEach((work) => {
      const key = getWorksCategoryFilterKey(work);
      const label = getWorksCategoryLabel(work);
      if (!key || !label) return;
      map.set(key, label);
    });

    const dynamic = Array.from(map.entries()).map(([key, label]) => ({
      key,
      label,
    }));

    // ラベルでソート（任意）
    dynamic.sort((a, b) => a.label.localeCompare(b.label, "ja"));

    return [{ key: "all", label: "ALL" }, ...dynamic];
  }, [works]);

  useEffect(() => {
    if (activeFilter === "all") {
      setFilteredWorks(works);
    } else {
      setFilteredWorks(
        works.filter((work) => getWorksCategoryFilterKey(work) === activeFilter)
      );
    }
  }, [activeFilter, works]);

  // 結果バッジ用のテキストを生成（簡易版）
  const getResultBadge = (work: Work): string => {
    const title = work.title || "";
    if (title.includes("350%") || title.includes("流入")) return "流入 350% UP";
    if (title.includes("CVR") || title.includes("2.5倍")) return "CVR 2.5倍";
    if (title.includes("CPA") || title.includes("-40%")) return "CPA -40%";
    if (title.includes("運用工数") || title.includes("1/3"))
      return "運用工数 1/3";
    if (title.includes("1位") || title.includes("獲得")) return "1位獲得";
    if (title.includes("リード") || title.includes("3倍"))
      return "リード数 3倍";
    return "成果あり";
  };

  // 統計情報を抽出（簡易版）
  const getStats = (work: Work) => {
    const title = work.title || "";
    const body = work.body || "";
    const text = title + " " + body;

    const stats: { label: string; value: string }[] = [];

    // パターンマッチングで統計を抽出
    const patterns = [
      { regex: /(\d+(?:\.\d+)?倍)/g, label: "改善率" },
      { regex: /(\d+(?:\.\d+)?%)/g, label: "改善率" },
      { regex: /(\d+位)/g, label: "順位" },
    ];

    patterns.forEach((pattern) => {
      const matches = text.match(pattern.regex);
      if (matches && matches.length > 0) {
        stats.push({ label: pattern.label, value: matches[0] });
      }
    });

    return stats.slice(0, 2); // 最大2つ
  };

  return (
    <>
      {/* Filter Section */}
      <div className={styles.filterSection}>
        <div className={styles.filterContainer}>
          <div className={styles.filterTabs}>
            {filters.map((filter) => (
              <button
                key={filter.key}
                className={`${styles.filterBtn} ${
                  activeFilter === filter.key ? styles.active : ""
                }`}
                onClick={() => setActiveFilter(filter.key)}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Works Grid */}
      <section className={styles.worksGridSection}>
        <div className={styles.worksGrid}>
          {filteredWorks.length > 0 ? (
            filteredWorks.map((work) => {
              const stats = getStats(work);
              return (
                <Link
                  key={work.id}
                  href={`/works/${work.id}`}
                  className={`${styles.workCard} fade-up`}
                  data-category={work.categoryKey}
                >
                  <div className={styles.workThumb}>
                    {work.thumbnail?.url ? (
                      <Image
                        src={work.thumbnail.url}
                        alt={work.title}
                        fill
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                        style={{ objectFit: "cover" }}
                      />
                    ) : (
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          background: "linear-gradient(45deg, #222, #333)",
                        }}
                      />
                    )}
                    <div className={styles.resultBadge}>
                      {getWorksCategoryLabel(work) || getResultBadge(work)}
                    </div>
                  </div>
                  <div className={styles.workInfo}>
                    <div className={styles.workMeta}>
                      {work.tag && (
                        <>
                          {Array.isArray(work.tag) ? (
                            work.tag.map((t, idx) => (
                              <span
                                key={idx}
                                className={`${styles.tag} ${styles.main}`}
                              >
                                {t.name}
                              </span>
                            ))
                          ) : (
                            <span className={`${styles.tag} ${styles.main}`}>
                              {work.tag.name}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                    <h3 className={styles.workTitle}>{work.title}</h3>
                    {stats.length > 0 && (
                      <div className={styles.workStats}>
                        {stats.map((stat, idx) => (
                          <div key={idx} className={styles.statItem}>
                            <strong>{stat.value}</strong>
                            {stat.label}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </Link>
              );
            })
          ) : (
            <div className={styles.emptyState}>
              <p>該当する実績がありません</p>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
